/**
 * Referências que vêm de fora: BibTeX, RIS e o que o Crossref devolve.
 *
 * Quem escreve trabalho acadêmico já tem as referências em algum lugar — no Zotero,
 * no Mendeley, no site da revista, no Google Acadêmico. Colar a referência como ela
 * vem é mais rápido (e muito mais seguro) do que redigitá-la campo a campo, e é aqui
 * que o texto de fora vira a **referência do gerador** (`ReferenciaDocumento`), que a
 * norma formata e a lista ordena.
 *
 * Três decisões que valem a pena saber:
 *
 * 1. **nada de adivinhar**: o que o formato não diz sai vazio, e não inventado. Campo
 *    vazio a pessoa completa no painel; campo errado ela não confere;
 * 2. **sem rede aqui**: este arquivo é leitura de texto (BibTeX e RIS) e a tradução do
 *    JSON do Crossref (`referenciaDeCrossref`). Quem busca na internet é o servidor
 *    (`POST /api/documentos/referencias/importar`), e assim o motor continua puro —
 *    testável sem rede, como o resto dele;
 * 3. **o tipo certo importa**: é o tipo que decide a forma da linha na norma (artigo de
 *    periódico não sai como livro). A tabela de cada formato está aqui, e é ela que os
 *    testes conferem;
 * 4. **o ISBN é outra porta**: livro publicado quase sempre vem por ISBN, e não por
 *    BibTeX. Quem busca o livro é a Open Library, do outro lado da rede (aqui só entra
 *    a leitura do que ela devolve, `referenciaDeOpenLibrary`);
 * 5. **a mesma obra não entra duas vezes**: `chaveDaReferencia` é a impressão digital
 *    que diz que duas linhas são o mesmo trabalho — é ela que avisa quem cola.
 */
/** Um DOI solto: `10.1590/1980-220x-reeusp-2024-0001` (com ou sem `https://doi.org/`). */
const DOI = /\b(10\.\d{4,9}\/[^\s"'<>]+)/i;
/** O DOI dentro do texto colado, quando há um. */
export function doiDoTexto(texto) {
    const achado = texto.match(DOI);
    if (!achado)
        return null;
    /* Ponto e vírgula no fim são da frase, não do DOI. */
    return achado[1].replace(/[.,;)]+$/, '');
}
/*
 * Um ISBN no meio do texto: 10 ou 13 dígitos, com hífen ou sem ele. O que separa um
 * ISBN de um telefone é o **dígito verificador** — e é ele que decide aqui: número
 * grande e comprido demais existe em qualquer lugar, ISBN válido não.
 */
/** Os dígitos de um ISBN, sem hífen nem espaço (o `X` final do ISBN-10 fica). */
function digitosDoIsbn(bruto) {
    return bruto.replace(/[\s-]/g, '').toUpperCase();
}
/**
 * O dígito verificador confere?
 *
 * O ISBN-10 fecha na conta módulo 11 (com `X` valendo 10 no fim), o ISBN-13 na módulo
 * 10 de pesos 1 e 3. É a mesma conta que a livraria faz: errar um dígito deixa de fechar.
 */
export function isbnValido(bruto) {
    const digitos = digitosDoIsbn(bruto);
    if (digitos.length === 10) {
        if (!/^\d{9}[\dX]$/.test(digitos))
            return false;
        let soma = 0;
        for (let i = 0; i < 10; i += 1) {
            const valor = digitos[i] === 'X' ? 10 : Number(digitos[i]);
            soma += valor * (10 - i);
        }
        return soma % 11 === 0;
    }
    if (digitos.length === 13) {
        let soma = 0;
        for (let i = 0; i < 13; i += 1)
            soma += Number(digitos[i]) * (i % 2 === 0 ? 1 : 3);
        return soma % 10 === 0;
    }
    return false;
}
/** O número tem cara de ISBN (10 dígitos ou 13 começando em 978/979)? */
function temFormaDeIsbn(digitos) {
    return /^\d{9}[\dX]$/.test(digitos) || /^97[89]\d{10}$/.test(digitos);
}
/**
 * O ISBN dentro do texto colado, só os dígitos.
 *
 * Preferência pelo número que **fecha a conta**: uma lista com vários números no meio
 * (o do exemplar, o do catálogo) entrega o ISBN de verdade primeiro. Se nenhum fechar,
 * mas um tiver a forma de ISBN, ele é devolvido assim mesmo — quem diz "não achei" é a
 * Open Library, e a mensagem dela ("confira o número") é mais útil do que "não
 * reconheci o texto".
 */
export function isbnDoTexto(texto) {
    const candidatos = [];
    for (const achado of texto.matchAll(/[\dXx][\dXx\s-]{7,20}[\dXx]/g)) {
        const digitos = digitosDoIsbn(achado[0]);
        if (!temFormaDeIsbn(digitos))
            continue;
        if (isbnValido(digitos))
            return digitos;
        candidatos.push(digitos);
    }
    return candidatos[0] ?? null;
}
/** O formato do texto colado — é ele que escolhe o leitor. */
export function formatoDeReferencias(texto) {
    const limpo = texto.trim();
    if (!limpo)
        return 'NENHUM';
    if (/^@\s*[a-z]+\s*[{(]/im.test(limpo))
        return 'BIBTEX';
    if (/^\s*TY\s{2}-\s/im.test(limpo))
        return 'RIS';
    return 'NENHUM';
}
/* ------------------------------------------------------------------ */
/* BibTeX                                                              */
/* ------------------------------------------------------------------ */
/**
 * O tipo do gerador a partir do tipo do BibTeX.
 *
 * A tradução é a que a norma espera: `article` é artigo de periódico; o que saiu num
 * livro ou em anais da conferência é **capítulo** (a norma trata igual: título do
 * trabalho dentro de uma obra maior); tese e dissertação são trabalho acadêmico;
 * `misc`/`online` são página da internet; o resto cai em livro, que é o caso comum.
 */
const TIPO_DO_BIBTEX = {
    article: 'ARTIGO',
    inproceedings: 'EVENTO',
    conference: 'EVENTO',
    incollection: 'CAPITULO',
    inbook: 'CAPITULO',
    book: 'LIVRO',
    booklet: 'LIVRO',
    manual: 'LIVRO',
    proceedings: 'EVENTO',
    phdthesis: 'TRABALHO',
    mastersthesis: 'TRABALHO',
    thesis: 'TRABALHO',
    techreport: 'TRABALHO',
    misc: 'SITE',
    online: 'SITE',
    electronic: 'SITE',
    www: 'SITE',
    /* Os suportes de mídia: o BibTeX não tem tipo próprio para eles, e é pelo `misc` que
       eles chegam — o `type` do campo diz o que é. */
    video: 'VIDEO',
    audio: 'AUDIO',
    movie: 'FILME',
    film: 'FILME',
    image: 'IMAGEM',
    legislation: 'LEGISLACAO',
};
/** O valor de um campo, sem as chaves ou aspas de fora e sem chaves internas. */
function valorLimpo(bruto) {
    let valor = bruto.trim();
    if ((valor.startsWith('{') && valor.endsWith('}')) ||
        (valor.startsWith('"') && valor.endsWith('"'))) {
        valor = valor.slice(1, -1);
    }
    return valor.replace(/[{}]/g, '').replace(/\s+/g, ' ').trim();
}
/** As páginas no formato da norma: `15--30` e `15-30` viram `15-30`. */
function paginasNormalizadas(paginas) {
    return paginas.replace(/\s*--+\s*/g, '-').replace(/\s+/g, '');
}
/**
 * Os autores do BibTeX: separados por ` and`, e cada um como veio ("Silva, João").
 *
 * Não há conversão de nome aqui: o motor já sabe ler as duas formas (com vírgula e sem
 * ela) na hora de montar a linha da norma — converter duas vezes seria perder a
 * informação de qual era o sobrenome.
 */
function autoresDoBibtex(campo) {
    return campo
        .split(/\s+and\s+/i)
        .map((autor) => valorLimpo(autor))
        .filter(Boolean);
}
/**
 * As entradas do BibTeX, com o corpo de cada uma.
 *
 * O corte é por **chaves contadas**, e não por `}` solto: o valor de um campo tem chaves
 * dentro (`title = {Um {T}ítulo}`), então procurar a primeira que fecha cortaria a
 * entrada no meio. Também aceita a entrada escrita numa linha só, que é como ela sai de
 * um campo de texto colado.
 */
function entradasDoBibtex(texto) {
    const entradas = [];
    const abre = /@([a-z]+)\s*[{(]/gi;
    let achado;
    while ((achado = abre.exec(texto))) {
        const marca = texto[achado.index + achado[0].length - 1];
        const fecha = marca === '{' ? '}' : ')';
        let profundidade = 1;
        let i = achado.index + achado[0].length;
        for (; i < texto.length && profundidade > 0; i += 1) {
            if (texto[i] === marca)
                profundidade += 1;
            else if (texto[i] === fecha)
                profundidade -= 1;
        }
        entradas.push({ tipo: achado[1] ?? '', corpo: texto.slice(achado.index + achado[0].length, i - 1) });
        abre.lastIndex = i;
    }
    return entradas;
}
/**
 * As referências de um texto BibTeX.
 *
 * A leitura é por entrada, e o corpo de cada uma é varrido campo a campo — o valor pode
 * ter vírgula dentro (título, autores), então o corte é pelo `=` e pelo nome do próximo
 * campo, e não por vírgula solta.
 */
export function referenciasDeBibtex(texto) {
    const referencias = [];
    for (const entrada of entradasDoBibtex(texto)) {
        const tipoDoFormato = entrada.tipo.toLowerCase();
        if (tipoDoFormato === 'string' || tipoDoFormato === 'comment' || tipoDoFormato === 'preamble') {
            continue;
        }
        /* O começo do corpo é a chave da entrada: `chave,` (o que vem antes da 1ª vírgula). */
        const campos = {};
        const resto = entrada.corpo.replace(/^[^,]*,\s*/, '');
        for (const achado of resto.matchAll(/([a-z][a-z0-9_-]*)\s*=\s*(\{[^{}]*(?:\{[^{}]*\}[^{}]*)*\}|"[^"]*"|[^,}\n]+)/gi)) {
            campos[(achado[1] ?? '').toLowerCase()] = achado[2] ?? '';
        }
        const campo = (nome) => valorLimpo(campos[nome] ?? '');
        const titulo = campo('title');
        if (!titulo)
            continue;
        const periodico = campo('journal') || campo('booktitle') || campo('journaltitle');
        const tipo = TIPO_DO_BIBTEX[tipoDoFormato] ?? 'LIVRO';
        const editora = campo('publisher') || campo('school') || campo('institution');
        const url = campo('url') || (campo('doi') ? `https://doi.org/${campo('doi')}` : '');
        /* A data completa quando ela veio (o `@article` do jornal traz). */
        const dataPublicacao = /^\d{4}-\d{2}-\d{2}/.test(campo('date'))
            ? campo('date').slice(0, 10)
            : '';
        referencias.push({
            tipo,
            autores: autoresDoBibtex(campo('author') || campo('editor')),
            titulo,
            local: localDoVeiculo(tipo, periodico, campo('address')),
            ...(editora ? { editora } : {}),
            ano: campo('year') || campo('date').slice(0, 4),
            ...(dataPublicacao ? { dataPublicacao } : {}),
            ...(campo('volume') ? { volume: campo('volume') } : {}),
            ...(campo('number') ? { numero: campo('number') } : {}),
            ...(campo('pages') ? { paginas: paginasNormalizadas(campo('pages')) } : {}),
            ...(url ? { url } : {}),
            ...(campo('edition') ? { edicao: campo('edition') } : {}),
            ...(tipo === 'TRABALHO' && campo('school') ? { instituicao: campo('school') } : {}),
        });
    }
    return referencias;
}
/* ------------------------------------------------------------------ */
/* RIS                                                                 */
/* ------------------------------------------------------------------ */
/** O tipo do gerador a partir do tipo do RIS. */
const TIPO_DO_RIS = {
    JOUR: 'ARTIGO',
    JFULL: 'ARTIGO',
    MGZN: 'REVISTA',
    NEWS: 'JORNAL',
    BOOK: 'LIVRO',
    EBOOK: 'LIVRO',
    CHAP: 'CAPITULO',
    ECHAP: 'CAPITULO',
    CONF: 'EVENTO',
    CPAPER: 'EVENTO',
    THES: 'TRABALHO',
    RPRT: 'TRABALHO',
    ELEC: 'SITE',
    WEB: 'SITE',
    BLOG: 'SITE',
    /* Mídia: o RIS tem tipo próprio para cada suporte. */
    VIDEO: 'VIDEO',
    SOUND: 'AUDIO',
    MUSIC: 'AUDIO',
    MPCT: 'FILME',
    ART: 'IMAGEM',
    PICT: 'IMAGEM',
    CHART: 'IMAGEM',
    /* Norma legal. */
    STAT: 'LEGISLACAO',
    LEG: 'LEGISLACAO',
    BILL: 'LEGISLACAO',
};
/**
 * As referências de um texto RIS (o formato do Zotero, do Mendeley e das revistas).
 *
 * Cada registro começa em `TY  - ` e termina em `ER  - `; os campos são duas letras,
 * dois espaços, um hífen e o valor. Campos que se repetem (autor, palavra-chave)
 * acumulam.
 */
export function referenciasDeRis(texto) {
    const referencias = [];
    let campos = {};
    let tipoDoFormato = '';
    const fechar = () => {
        if (!tipoDoFormato)
            return;
        const um = (nome) => (campos[nome]?.[0] ?? '').trim();
        const titulo = um('TI') || um('T1');
        const autores = [...(campos.AU ?? []), ...(campos.A1 ?? [])];
        if (titulo) {
            const tipo = TIPO_DO_RIS[tipoDoFormato] ?? 'LIVRO';
            const periodico = um('JO') || um('JF') || um('J2') || um('T2');
            const inicio = um('SP');
            const fim = um('EP');
            const doi = um('DO');
            const dataPublicacao = dataDoRis(um('DA'));
            referencias.push({
                tipo,
                autores,
                titulo,
                local: localDoVeiculo(tipo, periodico, um('CY')),
                ...(um('PB') ? { editora: um('PB') } : {}),
                ano: (um('PY') || um('Y1')).slice(0, 4),
                ...(dataPublicacao ? { dataPublicacao } : {}),
                ...(um('VL') ? { volume: um('VL') } : {}),
                ...(um('IS') ? { numero: um('IS') } : {}),
                ...(inicio || fim ? { paginas: inicio && fim ? `${inicio}-${fim}` : inicio || fim } : {}),
                ...(um('UR') || doi ? { url: um('UR') || `https://doi.org/${doi}` } : {}),
                ...(um('ET') ? { edicao: um('ET') } : {}),
                ...(tipo === 'TRABALHO' && um('PB') ? { instituicao: um('PB') } : {}),
            });
        }
        campos = {};
        tipoDoFormato = '';
    };
    for (const linha of texto.split(/\r?\n/)) {
        const achado = linha.match(/^\s*([A-Z][A-Z0-9])\s{2}-\s?(.*)$/);
        if (!achado)
            continue;
        const etiqueta = achado[1];
        const valor = (achado[2] ?? '').trim();
        if (etiqueta === 'TY') {
            fechar();
            tipoDoFormato = valor.toUpperCase();
            continue;
        }
        if (etiqueta === 'ER') {
            fechar();
            continue;
        }
        if (!tipoDoFormato)
            continue;
        campos[etiqueta] = [...(campos[etiqueta] ?? []), valor];
    }
    fechar();
    return referencias;
}
/** O tipo do gerador a partir do tipo do Crossref. */
const TIPO_DO_CROSSREF = {
    'journal-article': 'ARTIGO',
    'book-chapter': 'CAPITULO',
    'book-section': 'CAPITULO',
    'reference-entry': 'CAPITULO',
    book: 'LIVRO',
    monograph: 'LIVRO',
    'edited-book': 'LIVRO',
    'reference-book': 'LIVRO',
    dissertation: 'TRABALHO',
    'posted-content': 'SITE',
    report: 'SITE',
    dataset: 'SITE',
    'peer-review': 'ARTIGO',
    /* O artigo de anais de congresso e o desenho solto têm tipo próprio no Crossref. */
    'proceedings-article': 'EVENTO',
    figure: 'IMAGEM',
    standard: 'LEGISLACAO',
};
/** `2026, 10, 12` → `2026-10-12` (o formato que os campos de data do gerador usam). */
function dataIso(ano, mes, dia) {
    if (!ano || !mes || !dia)
        return '';
    const dois = (n) => String(n).padStart(2, '0');
    return `${ano}-${dois(mes)}-${dois(dia)}`;
}
/** O campo `DA` do RIS vem `2026/10/12`, às vezes com o resto da data depois da barra. */
function dataDoRis(bruto) {
    const casado = /^(\d{4})\/(\d{1,2})\/(\d{1,2})/.exec(bruto.trim());
    if (!casado)
        return '';
    return dataIso(Number(casado[1]), Number(casado[2]), Number(casado[3]));
}
/**
 * Os tipos em que a `local` é o nome do **veículo** que contém o trabalho — a revista, o
 * jornal, os anais do congresso —, e não a cidade da publicação.
 */
const TIPOS_COM_VEICULO = ['ARTIGO', 'CAPITULO', 'JORNAL', 'REVISTA', 'EVENTO'];
/**
 * O valor do campo `local` na importação.
 *
 * No jornal e na revista o nome **e** a cidade caem no mesmo campo (é assim que a NBR
 * escreve a linha), então eles se juntam aqui quando os dois vieram.
 */
function localDoVeiculo(tipo, veiculo, cidade) {
    if (!TIPOS_COM_VEICULO.includes(tipo))
        return cidade;
    const nome = veiculo.trim();
    if ((tipo === 'JORNAL' || tipo === 'REVISTA') && nome && cidade.trim()) {
        return `${nome}, ${cidade.trim()}`;
    }
    return nome;
}
/** O nome como o motor espera: "Silva, João" (e o nome solto quando não há sobrenome). */
function nomeDoAutor(autor) {
    if (autor.family && autor.given)
        return `${autor.family}, ${autor.given}`;
    return autor.family || autor.name || '';
}
/**
 * A referência a partir do JSON de uma obra do Crossref.
 *
 * É o que dá sentido ao "colar o DOI": a pessoa traz o número que está no artigo e o
 * gerador preenche autor, título, revista, volume, páginas e ano. Sem rede aqui — o
 * servidor é quem busca, e é ele quem passa o JSON para esta função, que assim pode ser
 * testada com uma obra guardada em vez de depender da internet.
 */
export function referenciaDeCrossref(obra) {
    const titulo = (obra.title?.[0] ?? '').trim();
    if (!titulo)
        return null;
    const tipo = TIPO_DO_CROSSREF[(obra.type ?? '').toLowerCase()] ?? 'ARTIGO';
    const autores = [...(obra.author ?? []), ...(obra.editor ?? [])]
        .map(nomeDoAutor)
        .filter(Boolean);
    const ano = obra.issued?.['date-parts']?.[0]?.[0];
    const contidoEm = obra['container-title']?.[0] ?? '';
    const doi = obra.DOI ?? '';
    /* A revista e o jornal trazem o dia no `issued`; o livro, só o ano. */
    const partesDaData = obra.issued?.['date-parts']?.[0];
    const dataPublicacao = partesDaData && partesDaData.length >= 3
        ? dataIso(partesDaData[0], partesDaData[1], partesDaData[2])
        : '';
    return {
        tipo,
        autores,
        titulo,
        ...(obra.subtitle?.[0] ? { subtitulo: obra.subtitle[0] } : {}),
        local: tipo === 'LIVRO' ? (obra['publisher-location'] ?? '') : contidoEm,
        ...(obra.publisher ? { editora: obra.publisher } : {}),
        ano: ano ? String(ano) : '',
        ...(dataPublicacao ? { dataPublicacao } : {}),
        ...(obra.volume ? { volume: obra.volume } : {}),
        ...(obra.issue ? { numero: obra.issue } : {}),
        ...(obra.page ? { paginas: paginasNormalizadas(obra.page) } : {}),
        ...(obra.URL || doi ? { url: obra.URL ?? `https://doi.org/${doi}` } : {}),
        ...(tipo === 'TRABALHO' && obra.institution?.[0]?.name
            ? { instituicao: obra.institution[0].name }
            : {}),
    };
}
/**
 * A referência a partir do livro que a Open Library devolve.
 *
 * Tudo o que ela não manda sai vazio, e não inventado: a cidade da publicação é o caso
 * clássico (o catálogo costuma ter, mas nem sempre), e campo vazio a pessoa completa.
 * O ano é o da **primeira** publicação — é ele que a norma pede, e não o da reimpressão
 * que a pessoa tem na mão.
 */
export function referenciaDeOpenLibrary(obra) {
    const titulo = (obra.title ?? '').trim();
    if (!titulo)
        return null;
    const editora = (obra.publisher ?? []).find((nome) => nome.trim()) ?? '';
    const cidade = (obra.publish_place ?? []).find((nome) => nome.trim()) ?? '';
    const ano = obra.first_publish_year ?? obra.publish_year?.[0];
    return {
        tipo: 'LIVRO',
        autores: (obra.author_name ?? []).map((nome) => nome.trim()).filter(Boolean),
        titulo,
        ...(obra.subtitle ? { subtitulo: obra.subtitle } : {}),
        local: cidade,
        ...(editora ? { editora } : {}),
        ano: ano ? String(ano) : '',
        ...(obra.key ? { url: `https://openlibrary.org${obra.key}` } : {}),
    };
}
/**
 * As referências de um texto colado, seja lá de onde ele venha.
 *
 * É a porta única do painel e da API: o formato é detectado, e o leitor certo entra.
 * Texto que não é nenhum dos dois vira lista vazia (quem chama avisa a pessoa).
 */
export function referenciasDoTexto(texto) {
    const formato = formatoDeReferencias(texto);
    if (formato === 'BIBTEX')
        return referenciasDeBibtex(texto);
    if (formato === 'RIS')
        return referenciasDeRis(texto);
    return [];
}
/** Sem acento e em minúsculas: "Alfabetizar com tela" e "alfabetizar com TELA" são a mesma obra. */
function semAcento(texto) {
    return texto.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}
/** As palavras de um texto, para comparar títulos sem depender da pontuação. */
function palavras(texto) {
    return semAcento(texto)
        .replace(/[^a-z0-9\s]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}
/** Quantas obras a busca devolve — a lista é para escolher, não para rolar. */
export const CANDIDATOS_MAXIMOS = 6;
/** Palavras que não dizem nada sobre a obra procurada. */
const PALAVRAS_VAZIAS = new Set([
    'a', 'o', 'as', 'os', 'um', 'uma', 'uns', 'umas', 'ao', 'aos', 'de', 'do', 'da', 'dos', 'das',
    'em', 'no', 'na', 'nos', 'nas', 'por', 'com', 'sem', 'sob', 'sobre', 'para', 'que', 'the', 'of',
    'and', 'in', 'on', 'to', 'for', 'with', 'an', 'le', 'la', 'les', 'el',
]);
/**
 * As palavras que contam numa consulta: sem acento, em minúsculas, fora as vazias e as
 * de menos de três letras.
 */
export function palavrasDaConsulta(consulta) {
    return palavras(consulta)
        .split(' ')
        .filter((palavra) => palavra.length >= 3 && !PALAVRAS_VAZIAS.has(palavra));
}
/**
 * O título da obra que voltou tem a ver com o que se procurou?
 *
 * A conta é generosa de propósito — a lista é uma **sugestão**, e quem escolhe é a
 * pessoa: perder o livro certo ("não achei") é pior do que mostrar um vizinho parecido.
 * O que não pode é entrar coisa que não é do assunto, e é isso que esta regra corta.
 *
 * A palavra casa por começo (`algorithm` acha `algorithms` *e* `algorithmic`). O corte
 * é **metade das palavras**, com o piso de duas: numa consulta de duas palavras, as duas
 * contam — procurando "Introduction to Algorithms", "Election Algorithms" casa uma só
 * (metade) e não é o livro; já "Uma introdução à linguística" para uma consulta de seis
 * palavras passa com três, que é o caso de quem cola o título inteiro e um pouco mais.
 */
export function combinaComOTitulo(referencia, consulta) {
    const procuradas = palavrasDaConsulta(consulta);
    if (procuradas.length === 0)
        return false;
    const noTitulo = palavras(`${referencia.titulo ?? ''} ${referencia.subtitulo ?? ''}`).split(' ');
    const achadas = procuradas.filter((procurada) => noTitulo.some((palavra) => palavra.startsWith(procurada) ||
        (procurada.length >= 4 && procurada.startsWith(palavra) && palavra.length >= 4))).length;
    const precisam = Math.max(2, Math.ceil(procuradas.length / 2));
    return achadas >= Math.min(precisam, procuradas.length);
}
/**
 * A lista de candidatos: as duas fontes juntas, sem repetida e sem o que não é do
 * assunto.
 *
 * A ordem é a da confiança: primeiro o Crossref (de onde saem os artigos e as teses, que
 * é o que um trabalho acadêmico cita), depois a Open Library (os livros) — e cada linha
 * diz de onde veio, porque uma obra pode voltar das duas.
 */
export function candidatosEncontrados(consulta, doCrossref, daOpenLibrary, maximo = CANDIDATOS_MAXIMOS) {
    const encontrados = [];
    const vistas = new Set();
    const juntar = (fonte, referencia) => {
        if (!referencia)
            return;
        if (!combinaComOTitulo(referencia, consulta))
            return;
        const chave = chaveDaReferencia(referencia) || `${fonte}|${palavras(referencia.titulo)}`;
        if (vistas.has(chave))
            return;
        vistas.add(chave);
        encontrados.push({ referencia, fonte });
    };
    for (const obra of doCrossref ?? [])
        juntar('CROSSREF', referenciaDeCrossref(obra));
    for (const livro of daOpenLibrary ?? [])
        juntar('OPENLIBRARY', referenciaDeOpenLibrary(livro));
    return encontrados.slice(0, maximo);
}
/* ------------------------------------------------------------------ */
/* A mesma obra duas vezes                                             */
/* ------------------------------------------------------------------ */
/**
 * O sobrenome de quem assina, nas duas formas que o motor aceita.
 *
 * `SILVA, João` e `João Silva` chegam ao mesmo `silva` — e é assim que a mesma obra
 * citada por dois exportadores diferentes é reconhecida como a mesma.
 */
function sobrenomeDe(autor) {
    const limpo = palavras(autor).trim();
    if (!limpo)
        return '';
    if (autor.includes(','))
        return limpo.split(' ')[0] ?? '';
    const partes = limpo.split(' ');
    return partes[partes.length - 1] ?? '';
}
/**
 * A "impressão digital" de uma referência: autor, título e ano, sem acento, em
 * minúsculas e sem pontuação.
 *
 * Serve para uma coisa só: dizer que duas linhas são **o mesmo trabalho**. É o que faz
 * o aviso na hora de colar — quem trouxe dez referências do Zotero volta a colar a mesma
 * lista amanhã, e a lista não pode dobrar de tamanho por isso.
 *
 * Quem não tem título não tem impressão digital (`''`): a referência "como eu escrevi"
 * (`texto`) entra como texto desde o começo, e nada aqui tenta adivinhar o que ela é.
 */
export function chaveDaReferencia(referencia) {
    const corpo = palavras(referencia.texto?.trim() ? referencia.texto : (referencia.titulo ?? ''));
    if (!corpo)
        return '';
    const autor = sobrenomeDe(referencia.autores?.[0] ?? '');
    const ano = (referencia.ano ?? '').replace(/\D/g, '');
    return [autor, corpo, ano].join('|');
}
/**
 * Marca, uma a uma, o que já está na lista.
 *
 * A repetição é conferida duas vezes: contra o que o documento **já tem** e contra o que
 * vem **no mesmo texto** (a lista de quem escreve às vezes traz a mesma obra duas vezes,
 * com o ano de uma edição e o da outra). O painel mostra a marca antes de acrescentar, e
 * quem cola decide.
 *
 * Cada item volta **como veio**, só com a marca acrescentada: quem chama pode passar a
 * referência sozinha ou já com a fonte de onde ela veio (`{ referencia, fonte }`), e nada
 * se perde no caminho.
 */
export function marcarReferenciasRepetidas(novas, jaNaLista) {
    const conhecidas = new Set(jaNaLista.map((referencia) => chaveDaReferencia(referencia)).filter(Boolean));
    return novas.map((referencia) => {
        const chave = chaveDaReferencia(referencia);
        const repetida = chave !== '' && conhecidas.has(chave);
        if (chave)
            conhecidas.add(chave);
        return { ...referencia, repetida };
    });
}
//# sourceMappingURL=referencias-importadas.js.map